<template>
  <div class="academic-checks-component">
    <div
      class="service-header mb-3 d-flex align-items-center justify-content-between"
    >
      <div>
        <h6 class="fs-6 fw-semibold text-dark mb-1 mt-0">
          Academic Credential Check
        </h6>
        <p class="text-xs text-muted mb-0 lh-sm">
          Verify academic credentials including certificate details and
          institution information.
        </p>
      </div>
    </div>

    <!-- Multiple Academic Entries -->
    <div
      v-for="(academic_entry, index) in academic_entries"
      :key="index"
      class="academic-entry mb-4"
    >
      <div
        class="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom"
      >
        <h6 class="fs-7 text-muted mb-0">
          Academic Information Entry {{ index + 1 }}
        </h6>
        <button
          type="button"
          class="btn btn-bg-danger rounded-3 btn-sm text-xs"
          @click="remove_academic_entry(index)"
          v-if="academic_entries.length > 1"
        >
          <i class="fas fa-times"></i>
          Remove
        </button>
      </div>

      <div class="row g-2 mb-2">
        <div class="col-md-12">
          <label class="form-label text-xs fw-medium text-secondary"
            >Is this for South African Matric Qualification?</label
          >
          <div class="d-flex align-items-center">
            <div class="form-check me-3">
              <input
                class="form-check-input"
                type="radio"
                :name="'is_matric_' + index"
                value="Yes"
                :id="'is_matric_yes_' + index"
                v-model="academic_entry.is_matric"
              />
              <label
                class="form-check-label text-xs"
                :for="'is_matric_yes_' + index"
                >Yes</label
              >
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                :name="'is_matric_' + index"
                :id="'is_matric_no_' + index"
                value="No"
                v-model="academic_entry.is_matric"
              />
              <label
                class="form-check-label text-xs"
                :for="'is_matric_no_' + index"
                >No</label
              >
            </div>
          </div>
        </div>
        <div class="col-md-12" v-if="academic_entry.is_matric === 'No'">
          <label class="form-label text-xs fw-medium text-secondary"
            >In what region was the qualification completed?</label
          >
          <select
            class="form-select form-select-sm text-xs"
            :id="'institution_region_' + index"
            v-model="academic_entry.institution_region"
          >
            <option value="" hidden>Select region</option>
            <option value="South Africa">South Africa</option>
            <option value="Africa">Africa</option>
            <option value="Global">Global</option>
          </select>
        </div>
      </div>

      <!-- Matric Checks -->
      <div class="row g-2" v-if="academic_entry.is_matric === 'Yes'">
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Year Completed <span class="text-danger">*</span></label
          >
          <input
            type="number"
            class="form-control form-control-sm text-xs"
            placeholder="Enter completion year"
            min="1900"
            :max="current_year"
            maxlength="4"
            @input="limit_year_length(index)"
            :id="'year_completed_' + index"
            v-model="academic_entry.year_completed"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Institution
            <span
              class="text-danger"
              >*</span
            >
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter institution name"
            v-model="academic_entry.institution"
            :id="'institution_' + index"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Qualification <span class="text-danger">*</span></label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            v-model="academic_entry.qualification"
            disabled
            :id="'qualification_' + index"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Select type of Matric Qualification. <span class="text-danger">*</span></label
          >
          <select
            class="form-select form-select-sm text-xs"
            :id="'doc_type_code_' + index"
            v-model="academic_entry.doc_type_code"
          >
            <option value="" hidden>Select Matric Qualification</option>
            <optgroup
              label="Matric Post 92"
              v-if="academic_entry.year_completed >= 1992"
            >
              <option value="11">
                Senior Certificate Grade 12 (1992-2007)
              </option>
              <option value="21">
                National Senior Certificate (NSC - from 2008+)
              </option>
            </optgroup>
            <optgroup
              label="Matric Pre 92"
              v-if="academic_entry.year_completed < 1992"
            >
              <option value="12">Subject Statement: Senior Certificate</option>
              <option value="22">
                Subject Statement: National Senior Certificate (NSC - from
                2008+)
              </option>
              <option value="31">
                Certificate: National Certificate (Vocational) Level 4 [NC(V)]
              </option>
              <option value="32">
                Subject Certificate: National Certificate (Vocational) Level 4
                [NC(V)]
              </option>
              <option value="41">Certificate: Technical College NSC</option>
              <option value="42">
                Subject Statement: Technical College N3/NSC
              </option>
              <option value="51">
                The General Education and Training Certificate for Adults (GETC:
                ABET Level 4)
              </option>
              <option value="52">
                Learning Area Certificate: GETC (ABET Level 4)
              </option>
              <option value="61">Certificate : N3</option>
              <option value="71">
                Certificate: National Certificate (Vocational) Level 3
              </option>
              <option value="72">
                Subject Statement: National Certificate (Vocational) Level 3
              </option>
              <option value="81">
                Certificate: National Certificate (Vocational) Level 2
              </option>
              <option value="82">
                Subject Statement: National Certificate (Vocational) Level 2
              </option>
            </optgroup>
          </select>
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Province Code
            <span
              class="text-danger"
              >*</span
            >
          </label>
          <select
            class="form-select form-select-sm text-xs"
            :id="'province_code_' + index"
            v-model="academic_entry.province_code"
          >
            <option value="" hidden>Select province</option>
            <option value="GAU">Gauteng</option>
            <option value="EC">Eastern Cape</option>
            <option value="KZN">KwaZulu-Natal</option>
            <option value="MPU">Mpumalanga</option>
            <option value="NW">North West Province</option>
            <option value="NC">Northern Cape</option>
            <option value="NP">Limpopo</option>
            <option value="FS">Free State</option>
            <option value="WC">Western Cape</option>
          </select>
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Certificate Number (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter certificate number"
            :id="'certificate_number_' + index"
            v-model="academic_entry.certificate_number"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Examination Number (Optional)</label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter examination number"
            :id="'examination_number_' + index"
            v-model="academic_entry.examination_number"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Name on Certificate (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter name as on certificate"
            :id="'name_on_certificate_' + index"
            v-model="academic_entry.name_on_certificate"
          />
        </div>
        <div class="col-md-12" v-if="academic_entry.year_completed && academic_entry.year_completed > 1900 && academic_entry.year_completed < 1992">
          <label class="file-upload-label mb-1">Certificate File <span
              class="text-danger"
              >*</span
            ></label>
          <hr class="mb-2 fw-light" />
          <div class="file-upload-container w-100 mx-auto">
            <div class="custom-file-upload">
              <div
                class="upload-header"
                :class="{ 'has-file': all_certificate_files[index].length > 0, 'drag-over': academic_entry.is_dragging_certificate }"
                @dragover.prevent="academic_entry.is_dragging_certificate = true"
                @dragleave.prevent="academic_entry.is_dragging_certificate = false"
                @drop.prevent="handle_certificate_file_drop(index, $event)"
              >
                <div v-if="all_certificate_files[index].length === 0" class="upload-placeholder">
                  <p class="mb-2 mt-3 fw-bold text-xs">
                    Drag and drop your certificate file here
                  </p>
                  <p class="text-muted small text-xxs mb-3">or</p>
                  <button
                    type="button"
                    class="btn btn-bg-green px-4 py-2 rounded-3 fw-bold text-xxs"
                    @click="$refs['certificate_input_' + index][0].click()"
                  >
                    Browse File
                  </button>
                  <p class="text-muted mt-3 small text-xxs">
                    Supported formats: PDF, JPG, JPEG, PNG
                  </p>
                  <p class="text-muted small text-xxs">
                    Maximum file size: 10MB
                  </p>
                </div>
                <div v-else class="selected-files-container">
                  <div class="my-1 text-center">
                    <p class="fw-bold text-xs">
                      <i class="fas fa-check-circle text-success me-2"></i>
                      {{ all_certificate_files[index].length }} file successfully uploaded
                    </p>
                  </div>
                  <div class="selected-files-list">
                    <div
                      v-for="(file, i) in all_certificate_files[index]"
                      :key="i"
                      class="selected-file-item mb-2"
                    >
                      <div class="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <i class="fas fa-file-alt me-2 text-primary flex-shrink-0"></i>
                        <span class="text-truncate file-name" :title="file.name"
                          >{{ file.name }}</span
                        >
                        <span
                          v-if="file.is_existing"
                          class="badge bg-info ms-2 text-xxs flex-shrink-0"
                          >Existing</span
                        >
                      </div>
                      <div class="file-actions">
                        <span class="file-size me-2 text-muted text-xxs"
                          >{{ format_file_size(file.size) }}</span
                        >
                        <button
                          type="button"
                          class="btn btn-sm text-primary p-0 me-2"
                          @click="view_certificate_file(file)"
                          title="View file"
                        >
                          <i class="fas fa-download"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm text-danger p-0"
                          @click="remove_certificate_file(index, i)"
                          title="Remove file"
                        >
                          <i class="fas fa-times-circle"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <input
                :ref="'certificate_input_' + index"
                type="file"
                class="d-none"
                @change="handle_certificate_file_change(index, $event)"
                accept=".pdf,.jpg,.jpeg,.png"
              />
            </div>
            <small
              v-if="academic_entry.certificate_error_message"
              class="form-text text-danger mt-2 d-block"
            >
              {{ academic_entry.certificate_error_message }}
            </small>
          </div>
        </div>
      </div>

      <!-- Region Checks - South Africa -->
      <div
        class="row g-2"
        v-else-if="
          academic_entry.is_matric === 'No' &&
          academic_entry.institution_region === 'South Africa'
        "
      >
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Institution <span class="text-danger">*</span></label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter institution name"
            :id="'institution_' + index"
            v-model="academic_entry.institution"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Qualification <span class="text-danger">*</span></label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter qualification or degree"
            :id="'qualification_' + index"
            v-model="academic_entry.qualification"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Qualification Type <span class="text-danger">*</span></label
          >
          <select
            class="form-select form-select-sm text-xs"
            :id="'qualification_type_' + index"
            v-model="academic_entry.qualification_type"
          >
            <option value="">Select qualification type</option>
            <option value="Short Course">Short Course</option>
            <option value="Degree">Degree</option>
            <option value="Diploma">Diploma</option>
            <option value="Certification">Certification</option>
            <option value="Attendance Certificate">
              Attendance Certificate
            </option>
            <option value="Trade Test Certificate">
              Trade Test Certificate
            </option>
          </select>
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Year Completed <span class="text-danger">*</span></label
          >
          <input
            type="number"
            class="form-control form-control-sm text-xs"
            placeholder="Enter completion year"
            min="1900"
            :max="current_year"
            @input="limit_year_length(index)"
            maxlength="4"
            :id="'year_completed_' + index"
            v-model="academic_entry.year_completed"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Country <span class="text-danger">*</span></label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            v-model="academic_entry.country"
            disabled
            :id="'country_' + index"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Province Code (Optional)</label
          >
          <select
            class="form-select form-select-sm text-xs"
            :id="'province_code_' + index"
            v-model="academic_entry.province_code"
          >
            <option value="" hidden>Select province</option>
            <option value="GAU">Gauteng</option>
            <option value="EC">Eastern Cape</option>
            <option value="KZN">KwaZulu-Natal</option>
            <option value="MPU">Mpumalanga</option>
            <option value="NW">North West Province</option>
            <option value="NC">Northern Cape</option>
            <option value="NP">Limpopo</option>
            <option value="FS">Free State</option>
            <option value="WC">Western Cape</option>
          </select>
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Certificate Number (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter certificate number"
            v-model="academic_entry.certificate_number"
            :id="'certificate_number_' + index"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Examination Number (Optional)</label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter examination number"
            v-model="academic_entry.examination_number"
            :id="'examination_number_' + index"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Name on Certificate (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter name as on certificate"
            v-model="academic_entry.name_on_certificate"
            :id="'name_on_certificate_' + index"
          />
        </div>
        <div class="col-md-12">
          <label class="file-upload-label mb-1">Certificate File <span class="text-danger">*</span></label>
          <hr class="mb-2 fw-light" />
          <div class="file-upload-container w-100 mx-auto">
            <div class="custom-file-upload">
              <div
                class="upload-header"
                :class="{ 'has-file': all_certificate_files[index].length > 0, 'drag-over': academic_entry.is_dragging_certificate }"
                @dragover.prevent="academic_entry.is_dragging_certificate = true"
                @dragleave.prevent="academic_entry.is_dragging_certificate = false"
                @drop.prevent="handle_certificate_file_drop(index, $event)"
              >
                <div v-if="all_certificate_files[index].length === 0" class="upload-placeholder">
                  <p class="mb-2 mt-3 fw-bold text-xs">
                    Drag and drop your certificate file here
                  </p>
                  <p class="text-muted small text-xxs mb-3">or</p>
                  <button
                    type="button"
                    class="btn btn-bg-green px-4 py-2 rounded-3 fw-bold text-xxs"
                    @click="$refs['certificate_input_' + index][0].click()"
                  >
                    Browse File
                  </button>
                  <p class="text-muted mt-3 small text-xxs">
                    Supported formats: PDF, JPG, JPEG, PNG
                  </p>
                  <p class="text-muted small text-xxs">
                    Maximum file size: 10MB
                  </p>
                </div>
                <div v-else class="selected-files-container">
                  <div class="my-1 text-center">
                    <p class="fw-bold text-xs">
                      <i class="fas fa-check-circle text-success me-2"></i>
                      {{ all_certificate_files[index].length }} file successfully uploaded
                    </p>
                  </div>
                  <div class="selected-files-list">
                    <div
                      v-for="(file, i) in all_certificate_files[index]"
                      :key="i"
                      class="selected-file-item mb-2"
                    >
                      <div class="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <i class="fas fa-file-alt me-2 text-primary flex-shrink-0"></i>
                        <span class="text-truncate file-name" :title="file.name"
                          >{{ file.name }}</span
                        >
                        <span
                          v-if="file.is_existing"
                          class="badge bg-info ms-2 text-xxs flex-shrink-0"
                          >Existing</span
                        >
                      </div>
                      <div class="file-actions">
                        <span class="file-size me-2 text-muted text-xxs"
                          >{{ format_file_size(file.size) }}</span
                        >
                        <button
                          type="button"
                          class="btn btn-sm text-primary p-0 me-2"
                          @click="view_certificate_file(file)"
                          title="View file"
                        >
                          <i class="fas fa-download"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm text-danger p-0"
                          @click="remove_certificate_file(index, i)"
                          title="Remove file"
                        >
                          <i class="fas fa-times-circle"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <input
                :ref="'certificate_input_' + index"
                type="file"
                class="d-none"
                @change="handle_certificate_file_change(index, $event)"
                accept=".pdf,.jpg,.jpeg,.png"
              />
            </div>
            <small
              v-if="academic_entry.certificate_error_message"
              class="form-text text-danger mt-2 d-block"
            >
              {{ academic_entry.certificate_error_message }}
            </small>
          </div>
        </div>
      </div>

      <!-- Region Checks - Africa -->
      <div
        class="row g-2"
        v-else-if="
          academic_entry.is_matric === 'No' &&
          academic_entry.institution_region === 'Africa'
        "
      >
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Institution <span class="text-danger">*</span></label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter institution name"
            :id="'institution_' + index"
            v-model="academic_entry.institution"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Qualification <span class="text-danger">*</span></label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter qualification or degree"
            :id="'qualification_' + index"
            v-model="academic_entry.qualification"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Qualification Type <span class="text-danger">*</span></label
          >
          <select
            class="form-select form-select-sm text-xs"
            v-model="academic_entry.qualification_type"
            :id="'qualification_type_' + index"
          >
            <option value="">Select qualification type</option>
            <option value="Short Course">Short Course</option>
            <option value="Degree">Degree</option>
            <option value="Diploma">Diploma</option>
            <option value="Certification">Certification</option>
            <option value="Attendance Certificate">
              Attendance Certificate
            </option>
            <option value="Trade Test Certificate">
              Trade Test Certificate
            </option>
          </select>
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Year Completed <span class="text-danger">*</span></label
          >
          <input
            type="number"
            class="form-control form-control-sm text-xs"
            placeholder="Enter completion year"
            min="1900"
            :max="current_year"
            @input="limit_year_length(index)"
            maxlength="4"
            v-model="academic_entry.year_completed"
            :id="'year_completed_' + index"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Country <span class="text-danger">*</span></label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter country"
            :id="'country_' + index"
            v-model="academic_entry.country"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Certificate Number (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter certificate number"
            :id="'certificate_number_' + index"
            v-model="academic_entry.certificate_number"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Examination Number (Optional)</label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter examination number"
            :id="'examination_number_' + index"
            v-model="academic_entry.examination_number"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Name on Certificate (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter name as on certificate"
            :id="'name_on_certificate_' + index"
            v-model="academic_entry.name_on_certificate"
          />
        </div>
        <div class="col-md-12">
          <label class="file-upload-label mb-1">Certificate File <span class="text-danger">*</span></label>
          <hr class="mb-2 fw-light" />
          <div class="file-upload-container w-100 mx-auto">
            <div class="custom-file-upload">
              <div
                class="upload-header"
                :class="{ 'has-file': all_certificate_files[index].length > 0, 'drag-over': academic_entry.is_dragging_certificate }"
                @dragover.prevent="academic_entry.is_dragging_certificate = true"
                @dragleave.prevent="academic_entry.is_dragging_certificate = false"
                @drop.prevent="handle_certificate_file_drop(index, $event)"
              >
                <div v-if="all_certificate_files[index].length === 0" class="upload-placeholder">
                  <p class="mb-2 mt-3 fw-bold text-xs">
                    Drag and drop your certificate file here
                  </p>
                  <p class="text-muted small text-xxs mb-3">or</p>
                  <button
                    type="button"
                    class="btn btn-bg-green px-4 py-2 rounded-3 fw-bold text-xxs"
                    @click="$refs['certificate_input_' + index][0].click()"
                  >
                    Browse File
                  </button>
                  <p class="text-muted mt-3 small text-xxs">
                    Supported formats: PDF, JPG, JPEG, PNG
                  </p>
                  <p class="text-muted small text-xxs">
                    Maximum file size: 10MB
                  </p>
                </div>
                <div v-else class="selected-files-container">
                  <div class="my-1 text-center">
                    <p class="fw-bold text-xs">
                      <i class="fas fa-check-circle text-success me-2"></i>
                      {{ all_certificate_files[index].length }} file successfully uploaded
                    </p>
                  </div>
                  <div class="selected-files-list">
                    <div
                      v-for="(file, i) in all_certificate_files[index]"
                      :key="i"
                      class="selected-file-item mb-2"
                    >
                      <div class="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <i class="fas fa-file-alt me-2 text-primary flex-shrink-0"></i>
                        <span class="text-truncate file-name" :title="file.name"
                          >{{ file.name }}</span
                        >
                        <span
                          v-if="file.is_existing"
                          class="badge bg-info ms-2 text-xxs flex-shrink-0"
                          >Existing</span
                        >
                      </div>
                      <div class="file-actions">
                        <span class="file-size me-2 text-muted text-xxs"
                          >{{ format_file_size(file.size) }}</span
                        >
                        <button
                          type="button"
                          class="btn btn-sm text-primary p-0 me-2"
                          @click="view_certificate_file(file)"
                          title="View file"
                        >
                          <i class="fas fa-download"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm text-danger p-0"
                          @click="remove_certificate_file(index, i)"
                          title="Remove file"
                        >
                          <i class="fas fa-times-circle"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <input
                :ref="'certificate_input_' + index"
                type="file"
                class="d-none"
                @change="handle_certificate_file_change(index, $event)"
                accept=".pdf,.jpg,.jpeg,.png"
              />
            </div>
            <small
              v-if="academic_entry.certificate_error_message"
              class="form-text text-danger mt-2 d-block"
            >
              {{ academic_entry.certificate_error_message }}
            </small>
          </div>
        </div>
      </div>

      <!-- Region Checks - Global -->
      <div
        class="row g-2"
        v-else-if="
          academic_entry.is_matric === 'No' &&
          academic_entry.institution_region === 'Global'
        "
      >
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Institution <span class="text-danger">*</span></label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter institution name"
            :id="'institution_' + index"
            v-model="academic_entry.institution"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Qualification <span class="text-danger">*</span></label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter qualification or degree"
            :id="'qualification_' + index"
            v-model="academic_entry.qualification"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Qualification Type <span class="text-danger">*</span></label
          >
          <select
            class="form-select form-select-sm text-xs"
            :id="'qualification_type_' + index"
            v-model="academic_entry.qualification_type"
          >
            <option value="">Select qualification type</option>
            <option value="Short Course">Short Course</option>
            <option value="Degree">Degree</option>
            <option value="Diploma">Diploma</option>
            <option value="Certification">Certification</option>
            <option value="Attendance Certificate">
              Attendance Certificate
            </option>
            <option value="Trade Test Certificate">
              Trade Test Certificate
            </option>
          </select>
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Year Completed <span class="text-danger">*</span></label
          >
          <input
            type="number"
            class="form-control form-control-sm text-xs"
            placeholder="Enter completion year"
            min="1900"
            :max="current_year"
            maxlength="4"
            @input="limit_year_length(index)"
            :id="'year_completed_' + index"
            v-model="academic_entry.year_completed"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Country <span class="text-danger">*</span></label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter country"
            :id="'country_' + index"
            v-model="academic_entry.country"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Certificate Number (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter certificate number"
            :id="'certificate_number_' + index"
            v-model="academic_entry.certificate_number"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Examination Number (Optional)</label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter examination number"
            :id="'examination_number_' + index"
            v-model="academic_entry.examination_number"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Name on Certificate (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter name as on certificate"
            :id="'name_on_certificate_' + index"
            v-model="academic_entry.name_on_certificate"
          />
        </div>
        <div class="col-md-12">
          <label class="file-upload-label mb-1">Certificate File <span class="text-danger">*</span></label>
          <hr class="mb-2 fw-light" />
          <div class="file-upload-container w-100 mx-auto">
            <div class="custom-file-upload">
              <div
                class="upload-header"
                :class="{ 'has-file': all_certificate_files[index].length > 0, 'drag-over': academic_entry.is_dragging_certificate }"
                @dragover.prevent="academic_entry.is_dragging_certificate = true"
                @dragleave.prevent="academic_entry.is_dragging_certificate = false"
                @drop.prevent="handle_certificate_file_drop(index, $event)"
              >
                <div v-if="all_certificate_files[index].length === 0" class="upload-placeholder">
                  <p class="mb-2 mt-3 fw-bold text-xs">
                    Drag and drop your certificate file here
                  </p>
                  <p class="text-muted small text-xxs mb-3">or</p>
                  <button
                    type="button"
                    class="btn btn-bg-green px-4 py-2 rounded-3 fw-bold text-xxs"
                    @click="$refs['certificate_input_' + index][0].click()"
                  >
                    Browse File
                  </button>
                  <p class="text-muted mt-3 small text-xxs">
                    Supported formats: PDF, JPG, JPEG, PNG
                  </p>
                  <p class="text-muted small text-xxs">
                    Maximum file size: 10MB
                  </p>
                </div>
                <div v-else class="selected-files-container">
                  <div class="my-1 text-center">
                    <p class="fw-bold text-xs">
                      <i class="fas fa-check-circle text-success me-2"></i>
                      {{ all_certificate_files[index].length }} file successfully uploaded
                    </p>
                  </div>
                  <div class="selected-files-list">
                    <div
                      v-for="(file, i) in all_certificate_files[index]"
                      :key="i"
                      class="selected-file-item mb-2"
                    >
                      <div class="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <i class="fas fa-file-alt me-2 text-primary flex-shrink-0"></i>
                        <span class="text-truncate file-name" :title="file.name"
                          >{{ file.name }}</span
                        >
                        <span
                          v-if="file.is_existing"
                          class="badge bg-info ms-2 text-xxs flex-shrink-0"
                          >Existing</span
                        >
                      </div>
                      <div class="file-actions">
                        <span class="file-size me-2 text-muted text-xxs"
                          >{{ format_file_size(file.size) }}</span
                        >
                        <button
                          type="button"
                          class="btn btn-sm text-primary p-0 me-2"
                          @click="view_certificate_file(file)"
                          title="View file"
                        >
                          <i class="fas fa-download"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm text-danger p-0"
                          @click="remove_certificate_file(index, i)"
                          title="Remove file"
                        >
                          <i class="fas fa-times-circle"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <input
                :ref="'certificate_input_' + index"
                type="file"
                class="d-none"
                @change="handle_certificate_file_change(index, $event)"
                accept=".pdf,.jpg,.jpeg,.png"
              />
            </div>
            <small
              v-if="academic_entry.certificate_error_message"
              class="form-text text-danger mt-2 d-block"
            >
              {{ academic_entry.certificate_error_message }}
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Academic Entry Button -->
    <div class="add-entry-section mt-3" v-if="academic_entries.length < 3">
      <button
        type="button"
        class="btn btn-bg-success rounded-3 btn-sm text-xs"
        @click="add_academic_entry"
      >
        <i class="fas fa-plus"></i>
        Add Another Academic Entry
      </button>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";
export default {
  name: "AcademicChecks",
  props: {
    service_id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      current_year: new Date().getFullYear(),
      academic_entries: [this.create_empty_academic_entry()],
    };
  },
  computed: {
    all_certificate_files() {
      const combined_certificate_files = {};
      this.academic_entries.forEach((academic_entry, index) => {
        combined_certificate_files[index] = [
          ...academic_entry.existing_certificate_files,
          ...academic_entry.selected_certificate_files,
        ];
      });
      return combined_certificate_files;
    },
  },
  methods: {
    limit_year_length(index) {
      let academic_entry = this.academic_entries[index];
      if (academic_entry.year_completed && academic_entry.year_completed.toString().length > 4) {
        academic_entry.year_completed = academic_entry.year_completed.toString().slice(0, 4);
      }
    },
    handle_certificate_file_change(index, event) {
      if (event.target.files && event.target.files.length > 0) {
        const certificate_file = event.target.files[0];
        const processed_certificate_files = [certificate_file];
        
        const current_total = this.all_certificate_files[index].length;
        if (current_total + processed_certificate_files.length > 1) {
          this.academic_entries[index].certificate_error_message = "Only 1 certificate file is allowed.";
          event.target.value = "";
          return;
        }
        const valid_types = [
          "application/pdf",
          "image/jpeg",
          "image/png",
          "image/jpg",
        ];
        const invalid_certificate_files = processed_certificate_files.filter(
          (file) => !valid_types.includes(file.type)
        );
        if (invalid_certificate_files.length > 0) {
          this.academic_entries[index].certificate_error_message = "Invalid file type. Only PDF, JPG, JPEG, PNG are allowed.";
          event.target.value = "";
          return;
        }
        const oversized_certificate_files = processed_certificate_files.filter(
          (file) => file.size > 50 * 1024 * 1024
        );
        if (oversized_certificate_files.length > 0) {
          this.academic_entries[index].certificate_error_message = "File size too large. Maximum size is 50MB.";
          event.target.value = "";
          return;
        }
        this.academic_entries[index].selected_certificate_files = [
          ...this.academic_entries[index].selected_certificate_files,
          ...processed_certificate_files,
        ];
        this.academic_entries[index].certificate_error_message = "";
        this.academic_entries[index].certificate_file = certificate_file;
      }
      event.target.value = "";
    },
    handle_certificate_file_drop(index, event) {
      this.academic_entries[index].is_dragging_certificate = false;
      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        const certificate_file = files[0];
        const processed_certificate_files = [certificate_file];
        const current_total = this.all_certificate_files[index].length;
        if (current_total + processed_certificate_files.length > 1) {
          this.academic_entries[index].certificate_error_message = "Only 1 certificate file is allowed.";
          return;
        }
        const valid_types = [
          "application/pdf",
          "image/jpeg",
          "image/png",
          "image/jpg",
        ];
        const valid_certificate_files = processed_certificate_files.filter((file) =>
          valid_types.includes(file.type)
        );
        if (valid_certificate_files.length === 0) {
          this.academic_entries[index].certificate_error_message = "Invalid file type. Only PDF, JPG, JPEG, PNG are allowed.";
          return;
        }
        const valid_sized_certificate_files = valid_certificate_files.filter(
          (file) => file.size <= 10 * 1024 * 1024
        );
        if (valid_sized_certificate_files.length === 0) {
          this.academic_entries[index].certificate_error_message = "File size too large. Maximum size is 10MB.";
          return;
        }
        if (valid_sized_certificate_files.length !== processed_certificate_files.length) {
          this.academic_entries[index].certificate_error_message = "Some files were too large and were not uploaded.";
        }
        this.academic_entries[index].selected_certificate_files = [
          ...this.academic_entries[index].selected_certificate_files,
          ...valid_sized_certificate_files,
        ];
        this.academic_entries[index].certificate_file = valid_sized_certificate_files[0];
      }
    },
    remove_certificate_file(index, file_index) {
      const all_files = this.all_certificate_files[index];
      const file_to_remove = all_files[file_index];
      
      // Remove from selected files (both new and existing converted files)
      const selected_index = this.academic_entries[index].selected_certificate_files.findIndex(
        (f) => f === file_to_remove
      );
      if (selected_index > -1) {
        this.academic_entries[index].selected_certificate_files.splice(selected_index, 1);
      }
      
      if (this.academic_entries[index].selected_certificate_files.length === 0) {
        this.academic_entries[index].certificate_file = null;
      }
    },
    view_certificate_file(file) {
      if (file.is_existing && file.document_url) {
        window.open(file.document_url, '_blank');
      } else {
        // For both new uploads and converted existing files
        const url = URL.createObjectURL(file);
        window.open(url, '_blank');
        URL.revokeObjectURL(url);
      }
    },
    format_file_size(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
      return (bytes / (1024 * 1024)).toFixed(2) + " MB";
    },
    validate_academic_entry(academic_entry, index) {
      // Remove previous error highlight
      const fields = [
        "institution",
        "qualification",
        "qualification_type",
        "year_completed",
        "country",
        "province_code",
        "doc_type_code",
      ];
      fields.forEach((field) => {
        const el = document.getElementById(`${field}_${index}`);
        if (el) el.classList.remove("border-danger");
      });
      if (academic_entry.is_matric === "") {
        Swal.fire({
          icon: "info",
          title: "Validation Error",
          text: "Academic record added with no data.",
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }
      // Matric validation
      if (academic_entry.is_matric === "Yes") {
        if (!academic_entry.year_completed) {
          const el = document.getElementById(`year_completed_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Please enter the year completed for Matric.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!academic_entry.institution) {
          const el = document.getElementById(`institution_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Please enter the institution for Matric.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!academic_entry.doc_type_code) {
          const el = document.getElementById(`doc_type_code_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Please select the type of Matric qualification.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!academic_entry.province_code) {
          const el = document.getElementById(`province_code_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Please select the province code for Matric.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (academic_entry.year_completed < 1992 && !academic_entry.certificate_file) {
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Certificate file is required for Matric completed before 1992.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
      }
      // Non-matric validation
      else if (academic_entry.is_matric === "No") {
        if (!academic_entry.institution_region) {
          const el = document.getElementById(`institution_region_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Please select the region where the qualification was completed.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!academic_entry.institution) {
          const el = document.getElementById(`institution_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Please enter the institution name.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!academic_entry.qualification) {
          const el = document.getElementById(`qualification_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Please enter the qualification or degree.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!academic_entry.qualification_type) {
          const el = document.getElementById(`qualification_type_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Please select the qualification type.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!academic_entry.year_completed) {
          const el = document.getElementById(`year_completed_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Please enter the year completed.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!academic_entry.country && academic_entry.institution_region !== "South Africa") {
          const el = document.getElementById(`country_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Please enter the country for non-South African qualifications.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
      }
      return true;
    },
    validate_all_entries() {
      for (let index = 0; index < this.academic_entries.length; index++) {
        if (!this.validate_academic_entry(this.academic_entries[index], index)) {
          return false;
        }
      }
      return true;
    },
    async handle_data_submit(){
      if (this.validate_all_entries()) {
        // Separate files from data
        try {
          const { data } = await this.separate_files_from_data();
          this.$emit("update_data", "academic", data);
          return true;
        } catch (error) {
          console.error("Error processing files:", error);
          return false;
        }
      }
      return false;
    },
    async separate_files_from_data() {
      const data = [];
      for (const academic_entry of this.academic_entries) {
        const academic_entry_data = { ...academic_entry };
        // Remove file-related properties from data
        delete academic_entry_data.selected_certificate_files;
        delete academic_entry_data.existing_certificate_files;
        delete academic_entry_data.certificate_file;
        delete academic_entry_data.is_dragging_certificate;
        delete academic_entry_data.certificate_error_message;
        // Convert selected files to base64
        if (academic_entry.selected_certificate_files && academic_entry.selected_certificate_files.length > 0) {
          const base64_certificate_files = [];
          for (const certificate_file of academic_entry.selected_certificate_files) {
            if (certificate_file instanceof File) {
              try {
                const base64 = await this.file_to_base64(certificate_file);
                base64_certificate_files.push({
                  name: certificate_file.name,
                  type: certificate_file.type,
                  size: certificate_file.size,
                  data: base64
                });
              } catch (error) {
                console.error("Error converting file to base64:", error);
              }
            }
          }
          academic_entry_data.certificate_files = base64_certificate_files;
        }
        // Keep existing files as they are (they should have URLs)
        if (academic_entry.existing_certificate_files && academic_entry.existing_certificate_files.length > 0) {
          academic_entry_data.existing_certificate_files = academic_entry.existing_certificate_files;
        }
        data.push(academic_entry_data);
      }
      return { data };
    },
    file_to_base64(certificate_file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(certificate_file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },
    base64_to_file(base64Data, fileName, mimeType) {
      // Extract the base64 data from data URL
      const base64 = base64Data.split(',')[1];
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new File([byteArray], fileName, { type: mimeType });
    },
    create_empty_academic_entry() {
      return {
        id: "",
        country: "",
        institution: "",
        qualification: "",
        qualification_type: "",
        year_completed: null,
        province_code: "",
        doc_type_code: "",
        certificate_number: "",
        examination_number: "",
        name_on_certificate: "",
        is_matric: "",
        institution_region: "",
        certificate_file: null,
        selected_certificate_files: [],
        existing_certificate_files: [],
        is_dragging_certificate: false,
        certificate_error_message: "",
      };
    },
    add_academic_entry() {
      if (this.academic_entries.length < 3) {
        this.academic_entries.push(this.create_empty_academic_entry());
      }
    },
    async remove_academic_entry(index) {
      const academic_entry = this.academic_entries[index];
      if (academic_entry.id) {
        const user_confirmed = await Swal.fire({
          icon: "warning",
          title: "Are you sure?",
          text: "This will permanently remove the academic entry and cannot be undone.",
          showCancelButton: true,
          confirmButtonText: "Yes, remove it",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
        });
        if (!user_confirmed.isConfirmed) return;
        const token = localStorage.getItem("token");
        try {
          const api_response = await fetch(`${API_BASE_URL}/verify_api/employee_screening/api/keys/remove/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${token}`,
            },
            body: JSON.stringify({ employee_screening_service_key_group_id: academic_entry.id }),
          });
          const response_data = await api_response.json();
          if (response_data.status === "success") {
            if (this.academic_entries.length > 1) {
              this.academic_entries.splice(index, 1);
            }
            Swal.fire({
              icon: "success",
              title: "Removed",
              text: "Academic entry removed successfully.",
              timer: 2000,
              showConfirmButton: false,
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: response_data.message || "Failed to remove academic entry.",
            });
          }
        } catch (api_error) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to remove academic entry.",
          });
        }
      } else {
        if (this.academic_entries.length > 1) {
          this.academic_entries.splice(index, 1);
        }
      }
    },
    fetch_data() {
      const token = localStorage.getItem("token");
      const workflow_id = localStorage.getItem("workflow_id") || null;
      if (!workflow_id) {
        console.error("Workflow ID is not set in localStorage.");
        return;
      }
      const query_params = new URLSearchParams({
        workflow_id: workflow_id,
        service_id: this.service_id
      });
      fetch(
        `${API_BASE_URL}/verify_api/employee_screening/api/get_sections_by_workflow_client/?${query_params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        }
      )
        .then((api_response) => api_response.json())
        .then((response_data) => {
          if (
            response_data.status === "success" &&
            response_data.data &&
            response_data.data.sections &&
            response_data.data.sections.length > 0
          ) {
            // Find the section for Academic Checks (or use the first section)
            const academic_section = response_data.data.sections.find(
              s => s.service_name === "Academic Checks"
            ) || response_data.data.sections[0];
            // Map key_groups to academic_entries
            if (
              academic_section &&
              academic_section.key_groups &&
              academic_section.key_groups.length > 0
            ) {
              this.academic_entries = academic_section.key_groups.map((key_group) => {
                // Flatten key_mappings into an object
                let academic_entry_data = {};
                if (key_group.key_mappings && Array.isArray(key_group.key_mappings)) {
                  key_group.key_mappings.forEach(mapping => {
                    academic_entry_data[mapping.key_name] = mapping.key_value;
                  });
                }
                let category_data = key_group.category || {};
                let institution_region = "";
                let is_matric_flag = "No";
                if (category_data.category_name){
                  if (category_data.category_name === "Matric") {
                    is_matric_flag = "Yes";
                  }
                  else if (category_data.category_name === "South African Only") {
                    institution_region = "South Africa";
                  } else if (category_data.category_name === "Africa") {
                    institution_region = "Africa";
                  } else if (category_data.category_name === "Global") {
                    institution_region = "Global";
                  }
                }
                // Set id and fallback for all fields
                const academic_entry = {
                  id: key_group.id || "",
                  country: academic_entry_data.country || "",
                  institution: academic_entry_data.institution || "",
                  qualification: academic_entry_data.qualification || "",
                  qualification_type: academic_entry_data.qualification_type || "",
                  year_completed: academic_entry_data.year_completed || "",
                  province_code: academic_entry_data.province_code || "",
                  doc_type_code: academic_entry_data.doc_type_code || "",
                  certificate_number: academic_entry_data.certificate_number || "",
                  examination_number: academic_entry_data.examination_number || "",
                  name_on_certificate: academic_entry_data.name_on_certificate || "",
                  is_matric: is_matric_flag,
                  institution_region: institution_region,
                  certificate_file: academic_entry_data.certificate_file || null,
                  selected_certificate_files: [],
                  existing_certificate_files: [],
                  is_dragging_certificate: false,
                  certificate_error_message: "",
                };

                // Process existing documents from API
                if (key_group.documents && Array.isArray(key_group.documents)) {
                  key_group.documents.forEach(doc => {
                    if (doc.document_path) {
                      try {
                        const file = this.base64_to_file(
                          doc.document_path,
                          doc.file_name || 'certificate.pdf',
                          doc.mime_type || 'application/pdf'
                        );
                        // Add metadata to the file object
                        file.is_existing = true;
                        file.document_id = doc.id;
                        // Add to selected_certificate_files so it appears as uploaded
                        academic_entry.selected_certificate_files.push(file);
                      } catch (error) {
                        console.error('Error converting base64 to file:', error);
                      }
                    }
                  });
                }

                return academic_entry;
              });
            } else {
              this.academic_entries = [this.create_empty_academic_entry()];
            }
          } else {
            this.academic_entries = [this.create_empty_academic_entry()];
          }
        })
        .catch((api_error) => {
          console.error("Error fetching academic checks:", api_error);
        });
    },
  },
  mounted() {
    this.fetch_data();
  },
  watch: {
    academic_entries: {
      handler(new_academic_entries) {
        new_academic_entries.forEach((academic_entry, index) => {
          // Set qualification to "Matric" if is_matric is "Yes"
          if (academic_entry.is_matric === "Yes") {
            this.academic_entries[index].qualification = "Matric";

            // Auto-set doc_type_code based on year_completed
            if (academic_entry.year_completed && Number(academic_entry.year_completed) >= 2008) {
              this.academic_entries[index].doc_type_code = "21";
            } else if (academic_entry.year_completed && Number(academic_entry.year_completed) >= 1992 && Number(academic_entry.year_completed) < 2008) {
              this.academic_entries[index].doc_type_code = "11";
            }
          }
          if (
            academic_entry.is_matric === "No" &&
            academic_entry.institution_region === "South Africa"
          ) {
            this.academic_entries[index].country = "South Africa";
          }
        });
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.fs-7 {
  font-size: 0.875rem;
}

.service-header {
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f1f3f4;
}

.form-label {
  margin-bottom: 0.25rem;
}

.form-control-sm {
  padding: 0.375rem 0.5rem;
}

.academic-entry {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
}

.add-entry-section {
  text-align: center;
}

.custom-file-upload {
  border: 2px dashed #ced4da;
  border-radius: 12px;
  transition: all 0.3s ease;
  min-height: 200px;
}

.custom-file-upload:hover {
  border-color: var(--primary-color);
}

.custom-file-upload.drag-over {
  border-color: var(--primary-color);
}

.upload-header {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 180px;
}

.upload-header.has-file {
  padding: 1.5rem;
  min-height: auto;
}

.upload-placeholder {
  text-align: center;
}

.selected-files-container {
  width: 100%;
}

.selected-files-list {
  max-height: 200px;
  overflow-y: auto;
  margin: 1rem 0;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

.selected-file-item {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.file-name {
  max-width: 200px;
  font-size: 0.875rem;
  font-weight: 500;
  word-break: break-word;
}

@media (max-width: 576px) {
  .selected-file-item {
    padding: 0.5rem;
  }
  
  .file-name {
    max-width: 150px;
    font-size: 0.8rem;
  }
}

.file-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.25rem;
}

.file-size {
  font-size: 0.75rem;
  white-space: nowrap;
}

@media (max-width: 576px) {
  .file-actions {
    width: 100%;
    justify-content: flex-end;
    margin-top: 0.25rem;
  }
  
  .file-size {
    margin-right: 0.5rem !important;
  }
}

.file-upload-container {
  margin-bottom: 1rem;
}

.file-upload-label{
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--bs-dark);
}

/* Temp CSS until radio buttons are fixed */
.form-check-label {
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  color: var(--bs-dark) !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  margin-left: 1.5rem !important;
}

/* File actions styling */
.file-actions button {
  transition: all 0.2s ease;
}

.file-actions button:hover {
  transform: scale(1.1);
}

.badge {
  font-size: 0.65rem;
  padding: 0.25rem 0.5rem;
}

/* Responsive styles for mobile */
@media (max-width: 576px) {
  .custom-file-upload {
    min-height: 180px;
  }
  
  .upload-header {
    padding: 1rem;
    min-height: 150px;
  }
  
  .upload-header.has-file {
    padding: 0.75rem;
  }
  
  .selected-files-list {
    max-height: 180px;
  }
  
  button.btn-bg-green {
    padding: 0.375rem 0.75rem !important;
  }
}
</style>
